import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, List, ListItem, ListItemText,
  ListItemButton, Typography, Box, CircularProgress,
  IconButton, Paper, Stepper, Step, StepLabel,
  InputAdornment, Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  Class as ClassIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { EDUGATE_API_URL } from '../config/serviceConfig';
import '../styles/CourseManagement.css';

const CourseEnrollmentDialog = ({ 
  open, 
  onClose, 
  currentUser,
  accentColor = '#4D8BFF',
  showNotification 
}) => {
  const { t } = useTranslation();
  const accent = accentColor || '#4D8BFF';
  // CAS用户获取student_id的工具函数
  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas', phone = '') => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Cas-User-ID': encodeURIComponent(employeeNumber),
        'X-User-Name': encodeURIComponent(userName),
        'X-User-Type': userType // ✅ 添加用户类型
      };
      
      const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
        method: 'POST',
        headers: headers
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.student_id;
        }
      }
      return null;
    } catch (error) {
      console.error('获取CAS用户student_id失败:', error);
      return null;
    }
  };

  // 获取或缓存用户的student_id
  const getUserStudentId = async () => {
    if (currentUser.student_id) return currentUser.student_id;
    
    if (currentUser.employeeNumber && currentUser.class_name === '统一认证用户') {
      const studentId = await getCasUserStudentId(
        currentUser.employeeNumber, 
        currentUser.name,
        currentUser.user_type || 'bupt_cas'
      );
      if (studentId) {
        currentUser.student_id = studentId;
        return studentId;
      }
    }
    return '';
  };

  const isCasUser = () => {
    return Boolean(currentUser && currentUser.employeeNumber && currentUser.class_name === '统一认证用户');
  };

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [studentInfo, setStudentInfo] = useState({
    name: '', contact: '', description: '', courseCode: ''
  });

  useEffect(() => {
    if (isCasUser() && currentUser.name) {
      setStudentInfo(prev => ({ ...prev, name: currentUser.name }));
    }
  }, [currentUser]);

  const resetDialog = () => {
    setStep(1);
    setSearchKeyword('');
    setCourses([]);
    setClasses([]);
    setSelectedCourse(null);
    setSelectedClass(null);
    setStudentInfo({
      name: isCasUser() ? currentUser.name : '',
      contact: '', description: '', courseCode: ''
    });
    setLoading(false);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const handleSearchCourses = async () => {
    if (!searchKeyword.trim()) {
      showNotification(t('course_enrollment_search_hint'), 'warning');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/courses/search?keyword=${encodeURIComponent(searchKeyword)}`);
      const data = await response.json();
      if (data.code === 0) {
        setCourses(data.data || []);
        if (data.data?.length > 0) setStep(2);
        else showNotification(t('course_enrollment_no_match'), 'info');
      } else {
        showNotification(data.message || t('course_enrollment_search_failed'), 'error');
      }
    } catch (error) {
      console.error('搜索课程错误:', error);
      showNotification(t('course_enrollment_retry'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCourse = async (course) => {
    if (!course.require_code) {
      showNotification(t('course_enrollment_not_open'), 'warning');
      return;
    }
    setSelectedCourse(course);
    setLoading(true);
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/courses/${course.id}/classes`);
      const data = await response.json();
      if (data.code === 0) {
        setClasses(data.data || []);
        if (data.data?.length > 0) setStep(3);
        else showNotification(t('course_enrollment_no_class'), 'info');
      } else {
        showNotification(data.message || t('course_enrollment_class_failed'), 'error');
      }
    } catch (error) {
      console.error('获取班级列表错误:', error);
      showNotification(t('course_enrollment_class_retry'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClass = (classItem) => {
    setSelectedClass(classItem);
    setStep(4);
  };

  const handleInfoChange = (field, value) => {
    setStudentInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitJoin = async () => {
    if (!studentInfo.name.trim()) {
      showNotification(t('course_enrollment_name_required'), 'warning');
      return;
    }
    if (selectedCourse?.require_code && !studentInfo.courseCode.trim()) {
      showNotification(t('course_enrollment_code_required'), 'warning');
      return;
    }
    const studentId = await getUserStudentId();
    if (!studentId) {
      showNotification(t('course_enrollment_auth_failed'), 'error');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        course_id: selectedCourse.id,
        class_id: selectedClass.id,
        student_name: studentInfo.name,
        contact: studentInfo.contact,
        description: studentInfo.description,
      };
      if (selectedCourse?.require_code && studentInfo.courseCode.trim()) {
        requestData.course_code = studentInfo.courseCode.trim();
      }

      const response = await fetch(`${EDUGATE_API_URL}/api/courses/join-v2?student_id=${studentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      const data = await response.json();
      if (data.code === 0) {
        showNotification(t('course_enrollment_join_success', { course: selectedCourse.name, className: selectedClass.name }), 'success');
        handleClose();
      } else {
        showNotification(data.message || t('course_enrollment_join_failed'), 'error');
      }
    } catch (error) {
      console.error('加入课程班级错误:', error);
      showNotification(t('course_enrollment_retry_generic'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SchoolIcon sx={{ fontSize: 38, color: accent, mb: 1.5 }} />
            <Typography variant="h6" sx={{ color: '#e7e7e7', mb: 1 }}>{t('course_search')}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 4 }}>
              {t('course_search_hint')}
            </Typography>
            <TextField
              fullWidth
              placeholder={t('course_search_placeholder')}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearchCourses()}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#292a2d',
                  color: '#e7e7e7',
                  borderRadius: '6px',
                  '& fieldset': { borderColor: '#3e3e42' },
                  '&:hover fieldset': { borderColor: accent },
                  '&.Mui-focused fieldset': { borderColor: accent }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      variant="contained" 
                      onClick={handleSearchCourses}
                      disabled={loading || !searchKeyword.trim()}
                      sx={{ 
                        minWidth: '80px',
                        borderRadius: '5px',
                        boxShadow: 'none',
                        background: accent
                      }}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : t('course_search_btn')}
                    </Button>
                  </InputAdornment>
                )
              }}
            />
          </Box>
        );
      case 2:
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
              {t('course_found', { count: courses.length })}
            </Typography>
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {courses.map((course) => (
                <ListItem key={course.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleSelectCourse(course)}
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.05)',
                      background: 'rgba(255,255,255,0.02)',
                      mb: 1,
                      '&:hover': { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    <Box sx={{ 
                      width: 40, height: 40, borderRadius: '50%', 
                      bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
                    }}>
                      <SchoolIcon fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ color: '#fff', fontWeight: 500 }}>{course.name}</Typography>
                          {course.require_code && (
                            <Chip label={t('course_need_code')} size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {course.parent_name ? `${t('course_belong')}：${course.parent_name}` : t('course_no_belong')}
                        </Typography>
                      }
                    />
                    <ArrowBackIcon sx={{ transform: 'rotate(180deg)', color: 'rgba(255,255,255,0.2)', fontSize: 18 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        );
      case 3:
        return (
          <Box>
            <Box sx={{ p: 2, mb: 2, borderRadius: '12px', bgcolor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
              <Typography variant="caption" sx={{ color: '#60a5fa', display: 'block', mb: 0.5 }}>{t('course_current')}</Typography>
              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>{selectedCourse?.name}</Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
              {t('course_select_class', { count: classes.length })}
            </Typography>
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {classes.map((classItem) => (
                <ListItem key={classItem.id} disablePadding>
                  <ListItemButton 
                    onClick={() => handleSelectClass(classItem)}
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid rgba(255,255,255,0.05)',
                      background: 'rgba(255,255,255,0.02)',
                      '&:hover': { background: 'rgba(255,255,255,0.05)' }
                    }}
                  >
                    <Box sx={{ 
                      width: 36, height: 36, borderRadius: '50%', 
                      bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10b981',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2
                    }}>
                      <ClassIcon fontSize="small" />
                    </Box>
                    <ListItemText primary={<Typography sx={{ color: '#fff' }}>{classItem.name}</Typography>} />
                    <ArrowBackIcon sx={{ transform: 'rotate(180deg)', color: 'rgba(255,255,255,0.2)', fontSize: 18 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        );
      case 4:
        return (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{t('course_enrollment_course_label')}</Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>{selectedCourse?.name}</Typography>
              </Box>
              <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{t('course_enrollment_class_label')}</Typography>
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>{selectedClass?.name}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label={t('course_enrollment_name')}
                value={studentInfo.name}
                onChange={(e) => handleInfoChange('name', e.target.value)}
                disabled={isCasUser()}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                }}
                helperText={isCasUser() ? t('course_enrollment_cas_hint') : ""}
              />
              <TextField
                fullWidth
                label={t('course_enrollment_contact')}
                value={studentInfo.contact}
                onChange={(e) => handleInfoChange('contact', e.target.value)}
                placeholder={t('course_enrollment_phone_placeholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' }
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
                }}
              />
              {selectedCourse?.require_code && (
                <TextField
                  fullWidth
                  required
                  label={t('course_enrollment_code')}
                  value={studentInfo.courseCode}
                  onChange={(e) => handleInfoChange('courseCode', e.target.value)}
                  placeholder={t('course_enrollment_code_placeholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      bgcolor: 'rgba(245, 158, 11, 0.05)',
                      '& fieldset': { borderColor: 'rgba(245, 158, 11, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(245, 158, 11, 0.5)' },
                      '&.Mui-focused fieldset': { borderColor: '#f59e0b' }
                    },
                    '& .MuiInputLabel-root': { color: '#f59e0b' }
                  }}
                />
              )}
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="workbench-dialog course-enrollment-dialog"
      PaperProps={{
        className: 'workbench-dialog-paper',
        style: { '--workbench-accent': accent },
        sx: {
          borderRadius: '10px',
          background: '#1e1e1e',
          border: '1px solid #3e3e42',
          color: '#e7e7e7',
          minHeight: '380px'
        }
      }}
    >
      <DialogTitle className="workbench-dialog-title" sx={{ borderTop: `2px solid ${accent}`, borderBottom: '1px solid #3e3e42', px: 3, py: 1.75 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            {step > 1 && (
              <IconButton onClick={handleBack} size="small" sx={{ color: '#a6afb8', borderRadius: '5px', '&:hover': { color: '#fff', bgcolor: '#303238' } }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {step === 1 ? t('course_enrollment_join_new') : step === 4 ? t('course_enrollment_fill_info') : t('course_enrollment_select_course')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: '#a6afb8', borderRadius: '5px', '&:hover': { color: '#fff', bgcolor: '#303238' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 1, mt: 1.5 }}>
          <Stepper activeStep={step - 1} alternativeLabel 
            sx={{ 
              '& .MuiStepLabel-label': { color: 'rgba(255,255,255,0.3) !important' },
              '& .MuiStepLabel-label.Mui-active': { color: `${accent} !important` },
              '& .MuiStepLabel-label.Mui-completed': { color: '#c3c9d0 !important' },
              '& .MuiStepIcon-root': { color: '#3e3e42' },
              '& .MuiStepIcon-root.Mui-active': { color: accent },
              '& .MuiStepIcon-root.Mui-completed': { color: accent },
            }}
          >
            {[t('course_enrollment_search_btn'), t('course_enrollment_course_label'), t('course_enrollment_class_label'), t('course_enrollment_join_btn')].map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>
      </DialogTitle>

      <DialogContent className="workbench-dialog-content" sx={{ p: 3, minHeight: '280px' }}>
        {loading && step !== 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress sx={{ color: accent }} />
          </Box>
        ) : (
          renderStepContent()
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #3e3e42', background: '#202124' }}>
        {step === 4 && (
          <Button
            variant="contained"
            onClick={handleSubmitJoin}
            disabled={loading || !studentInfo.name.trim() || (selectedCourse?.require_code && !studentInfo.courseCode.trim())}
            fullWidth
            sx={{
              bgcolor: accent,
              py: 1,
              fontSize: '1rem',
              fontWeight: 600,
              '&:hover': { bgcolor: accent, filter: 'brightness(0.92)' }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('course_enrollment_confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CourseEnrollmentDialog;